import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  IconButton,
  TablePagination,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Card,
  CardContent,
  InputAdornment,
  Tooltip,
  Tabs,
  Tab,
  Grid,
  Avatar,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Skeleton,
  Alert,
  Autocomplete,
  Checkbox,
  FormControlLabel,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationOnIcon,
  AttachMoney as MoneyIcon,
  Star as StarIcon,
  WhatsApp as WhatsAppIcon,
  Person as PersonIcon,
  EventAvailable as VisitIcon,
  Schedule as FollowUpIcon,
  Timeline as TimelineIcon,
  Refresh as RefreshIcon,
  Business as BusinessIcon,
  SwapHoriz as TransferIcon,
  History as HistoryIcon,
  CloudUpload as CloudUploadIcon,
  Tune as TuneIcon,
} from '@mui/icons-material';
import { useHistory } from 'react-router-dom';
import AlertModal from '../../components/AlertModal';
import ConfirmModal from '../../components/ConfirmModal';
import { leadService } from '../../services/lead';
import { siteVisitService } from '../../services/siteVisit';
import { getAssociates } from '../../services/associate';
import { getBudgets, getSources, getInquiryStatuses } from '../../services/master';
import DataStateHandler from '../../components/Loader/DataStateHandler';
import { hasReadPermission, hasWritePermission } from '../../utils/menuFilter';
import WarningIcon from '@mui/icons-material/Warning';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import ExportButton from '../../components/ExportButton/ExportButton';
import RefreshButton from '../../components/RefreshButton/RefreshButton';
import { exportAsPDF, exportAsCSV, exportAsExcel } from '../../utils/importExport';
const mapLead = (l) => {
  const nextFollowUpDate = l.followUp?.nextFollowUpDate;
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  let isTodayFollowUp = false;
  let isPendingFollowUp = false;

  if (nextFollowUpDate) {
    const fDate = new Date(nextFollowUpDate);
    const fDateOnly = new Date(fDate);
    fDateOnly.setHours(0, 0, 0, 0);

    if (fDateOnly.getTime() === today.getTime()) {
      isTodayFollowUp = true;
    } else if (fDateOnly.getTime() < today.getTime()) {
      isPendingFollowUp = true;
    }
  }

  return {
    id: l._id,
    leadId: `LD${l.leadId ?? ''}`,
    leadNo: l.leadId,
    name: l.customerDetails?.name ?? '-',
    email: l.customerDetails?.email ?? '-',
    phone: l.customerDetails?.phone ?? '-',
    whatsapp: l.customerDetails?.whatsapp ?? '-',
    company: l.customerDetails?.company ?? l.createdBy ?? '-',
    designation: l.customerDetails?.profession ?? '-',
    source: l.source ?? '-',
    status: l.followUp?.lastFollowUpStatus ?? l.latestFollowUpStatus ?? l.status ?? '-',
    priority: l.followUp?.lastFollowUpStatus ?? l.latestFollowUpStatus ?? 'New',
    interestedIn: l.interestedIn ?? '-',
    budget: l.budgetUpto ?? '-',
    location: l.address?.addressLine ?? '-',
    city: l.address?.city ?? '-',
    state: l.address?.state ?? '-',
    assignedTo: l.associateDetails?.name ?? '-',
    assignedEmail: l.associateDetails?.email ?? '-',
    assignedMobile: l.associateDetails?.phone ?? '-',
    associateId: l.associateId ?? null,
    createdDate: l.createdAt ? new Date(l.createdAt).toLocaleDateString('en-IN') : '-',
    lastContact: l.followUp?.lastFollowUpDate
      ? new Date(l.followUp.lastFollowUpDate).toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      })
      : '-',
    nextFollowUp: nextFollowUpDate
      ? new Date(nextFollowUpDate).toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      })
      : '-',
    notes: l.followUp?.lastFollowUpRemark ?? l.remarks?.generalRemark ?? '-',
    requirement: [l.remarks?.remark1, l.remarks?.remark2]
      .filter(Boolean)
      .join(' | '),
    rating: 0,
    industry: l.industry ?? '-',
    companySize: l.companySize ?? '-',
    website: l.website ?? '-',
    address: l.address?.addressLine ?? '-',
    score: l.score ?? 0,
    leadScore: l.leadScore ?? 0,
    isExistingCustomer: !!l.customerId,
    customerId: l.customerId ?? null,
    isTodayFollowUp,
    timelineCount: l.timelineCount ?? 0,
    isPendingFollowUp,
    raw: l,
  };
};

const LeadCardSkeleton = ({ count = 6 }) => (
  <Grid container spacing={2} sx={{ mb: 3 }}>
    {Array.from({ length: count }).map((_, index) => (
      <Grid item xs={12} key={`lead-skeleton-${index}`}>
        <Card
          sx={{
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ p: 2, backgroundColor: '#eef2ff' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <Skeleton variant="text" width="40%" height={20} />
                <Skeleton variant="text" width="60%" height={28} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Skeleton variant="text" width="80%" height={24} />
              </Grid>
              <Grid item xs={12} sm={4} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
                <Skeleton variant="rounded" width={80} height={26} />
              </Grid>
            </Grid>
          </Box>
          <CardContent sx={{ pt: 2, pb: 1.5, px: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', gap: 2, p: 1 }}>
                  <Skeleton variant="circular" width={96} height={96} />
                  <Box sx={{ flex: 1 }}>
                    <Skeleton variant="text" width="70%" height={28} />
                    <Skeleton variant="text" width="50%" height={22} />
                    <Skeleton variant="text" width="60%" height={22} />
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                      <Skeleton variant="rounded" width={70} height={22} />
                      <Skeleton variant="rounded" width={80} height={22} />
                    </Box>
                    <Divider sx={{ my: 1.5 }} />
                    <Skeleton variant="text" width="90%" height={20} />
                    <Skeleton variant="text" width="80%" height={20} />
                    <Skeleton variant="text" width="75%" height={20} />
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ p: 1 }}>
                  <Skeleton variant="text" width="50%" height={22} />
                  <Skeleton variant="text" width="90%" height={18} />
                  <Skeleton variant="text" width="85%" height={18} />
                  <Skeleton variant="text" width="80%" height={18} />
                  <Skeleton variant="text" width="88%" height={18} />
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ p: 1 }}>
                  <Skeleton variant="text" width="50%" height={22} />
                  <Skeleton variant="text" width="90%" height={18} />
                  <Skeleton variant="text" width="80%" height={18} />
                  <Skeleton variant="text" width="85%" height={18} />
                </Box>
              </Grid>
            </Grid>
          </CardContent>
          <Box
            sx={{
              px: 2,
              pb: 1.5,
              pt: 1.5,
              display: 'flex',
              gap: 1,
              borderTop: '1px solid #f0f0f0',
              flexWrap: 'wrap',
            }}
          >
            <Skeleton variant="rounded" width={40} height={32} />
            <Skeleton variant="rounded" width={40} height={32} />
            <Skeleton variant="rounded" width={100} height={32} />
            <Skeleton variant="rounded" width={100} height={32} />
            <Skeleton variant="rounded" width={100} height={32} />
            <Skeleton variant="rounded" width={40} height={32} />
            <Skeleton variant="rounded" width={40} height={32} />
            <Skeleton variant="rounded" width={40} height={32} />
          </Box>
        </Card>
      </Grid>
    ))}
  </Grid>
);

function useLeads({ page, limit, search, status, source, stage, visitType, followupFilter, sortOrder, assignmentFilter, associateId }) {
  const [state, setState] = useState({
    data: [],
    totalDocs: 0,
    totalPages: 0,
    loading: false,
    error: '',
  });

  const fetchLeads = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: '' }));
    try {
      const params = {
        page: page + 1,
        limit,
      };

      if (search && search.trim()) params.filterKey = search.trim();
      if (status && status !== 'all' && status !== 'All Leads') params.status = status;
      if (source && source !== 'all') params.source = source;
      if (stage && stage !== 'all') params.stage = stage;
      if (visitType) params.visitType = visitType;
      if (followupFilter) params.followupFilter = followupFilter;
      if (sortOrder && sortOrder !== 'all') params.sortOrder = sortOrder;
      if (assignmentFilter && assignmentFilter !== 'all') params.assignmentFilter = assignmentFilter;
      if (associateId) params.associateId = associateId;

      const data = await leadService.getLeads(params);
      if (!data) throw new Error('Invalid response');
      const list = (Array.isArray(data.docs) ? data.docs : []).map(mapLead);

      setState({
        data: list,

        totalDocs: data.totalDocs || 0,
        totalPages: data.totalPages || 1,
        loading: false,
        error: '',
      });
    } catch (e) {
      setState((s) => ({
        ...s,
        loading: false,
        error: e.message || 'Failed to load leads',
      }));
    }
  }, [page, limit, search, status, source, stage, visitType, followupFilter, sortOrder, assignmentFilter, associateId]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  return { ...state, refetch: fetchLeads };
}

// Reset Button Component
const ResetButton = ({ onClick, disabled }) => (
  <Button
    variant="outlined"
    color="error"
    startIcon={<RefreshIcon />}
    onClick={onClick}
    disabled={disabled}
    fullWidth
    sx={{
      height: '56px',
      textTransform: 'none',
      fontWeight: 600,
      fontSize: '13px',
      borderRadius: 2,
      borderColor: '#d32f2f',
      '&:hover': {
        borderColor: '#b71c1c',
        backgroundColor: 'rgba(211, 47, 47, 0.04)',
      },
    }}
  >
    Reset
  </Button>
);

const LeadList = () => {
  const history = useHistory();
  const canReadLead = hasReadPermission('lead.lead-list') || hasReadPermission('lead') || hasReadPermission('lead-list');
  const canWriteLead = hasWritePermission('lead.lead-list') || hasWritePermission('lead') || hasWritePermission('lead-list');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [stageFilter, setStageFilter] = useState('all');
  const [visitTypeFilter, setVisitTypeFilter] = useState('');
  const [followupFilter, setFollowupFilter] = useState('');
  const [sortOrder, setSortOrder] = useState('all');
  const [assignmentFilter, setAssignmentFilter] = useState('all');
  const [selectedAssociateFilter, setSelectedAssociateFilter] = useState(null);
  const [filterAssociateOptions, setFilterAssociateOptions] = useState([]);
  const [loadingFilterAssociates, setLoadingFilterAssociates] = useState(false);
  const [filterAssociateSearchTerm, setFilterAssociateSearchTerm] = useState('');
  const [inquiryStatuses, setInquiryStatuses] = useState([]);
  const [sources, setSources] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [cardTabValue, setCardTabValue] = useState({});
  const [followUpDialogOpen, setFollowUpDialogOpen] = useState(false);
  const [followUpData, setFollowUpData] = useState({
    followUpDate: '',
    nextFollowUpDate: '',
    inquiryStatus: '',
    budgetUpto: '',
    remark: '',
  });
  const [budgetOptions, setBudgetOptions] = useState([]);
  const [followUpHistory, setFollowUpHistory] = useState([]);
  const [timelineDialogOpen, setTimelineDialogOpen] = useState(false);
  const [timelineData, setTimelineData] = useState([]);
  const [followUpError, setFollowUpError] = useState('');
  const [followUpSuccess, setFollowUpSuccess] = useState('');
  const [followUpLoading, setFollowUpLoading] = useState(false);
  const [followUpSaving, setFollowUpSaving] = useState(false);
  const [followUpPickerOpen, setFollowUpPickerOpen] = useState(false);
  const [nextFollowUpPickerOpen, setNextFollowUpPickerOpen] = useState(false);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [transferLead, setTransferLead] = useState(null);
  const [associateDirectory] = useState([]);
  const [assigneeDirectory, setAssigneeDirectory] = useState([]);
  const [loadingAssignees, setLoadingAssignees] = useState(false);
  const [assigneeSearchTerm, setAssigneeSearchTerm] = useState('');
  const [selectedAssociate, setSelectedAssociate] = useState(null);
  const [transferRemark, setTransferRemark] = useState('');
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assignRemark, setAssignRemark] = useState('');
  const [assignLoading, setAssignLoading] = useState(false);
  const [selectedAssignee, setSelectedAssignee] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [statusColors, setStatusColors] = useState({});
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const [alertData, setAlertData] = useState({ open: false, message: '', type: 'info' });
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    confirmText: '',
    cancelText: '',
    type: 'warning',
    action: null
  });

  const showAlert = (message, type = 'info') => setAlertData({ open: true, message, type });

  const searchTimeoutRef = useRef(null)

  const buildExportRows = (leads) => {
    return (leads || []).map((l) => ({
      'Lead ID': l.leadId || '-',
      'Customer Name': l.name || '-',
      'Phone': l.phone || '-',
      'Company': l.company || '-',
      'Source': l.source || '-',
      'Status': l.status || '-',
      'Assigned To': l.assignedTo || '-',
      'Created Date': l.createdDate || '-',
      'Budget': l.budget || '-',
    }));
  };

  const handleExportPdf = () => {
    const rows = buildExportRows(data);
    exportAsPDF({
      rows,
      title: 'Lead List Report',
      fileNameBase: 'Leads_Report'
    });
  };

  const handleExportCsv = () => {
    const rows = buildExportRows(data);
    exportAsCSV({
      rows,
      fileNameBase: 'Leads_Export'
    });
  };

  const handleExportExcel = () => {
    const rows = buildExportRows(data);
    exportAsExcel({
      rows,
      sheetName: 'Leads',
      fileNameBase: 'Leads_Export'
    });
  };

  const { data, totalDocs, totalPages, loading, error, refetch } = useLeads({
    page,
    limit: rowsPerPage,
    search,
    status: statusFilter,
    source: sourceFilter,
    stage: stageFilter,
    visitType: visitTypeFilter,
    followupFilter: followupFilter,
    sortOrder: sortOrder,
    assignmentFilter: assignmentFilter,
    associateId: selectedAssociateFilter?._id,
  });

  const newCount = useMemo(
    () => data.filter((r) => r.status === 'New').length,
    [data],
  );
  const contactedCount = useMemo(
    () => data.filter((r) => r.status === 'Contacted').length,
    [data],
  );
  const qualifiedCount = useMemo(
    () => data.filter((r) => r.status === 'Qualified').length,
    [data],
  );
  const todayFollowUpCount = useMemo(
    () => data.filter((r) => r.isTodayFollowUp).length,
    [data],
  );
  const pendingFollowUpCount = useMemo(
    () => data.filter((r) => r.isPendingFollowUp).length,
    [data],
  );

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleSearchInputChange = useCallback((e) => {
    setSearchInput(e.target.value);
  }, []);

  const handleSearchClick = useCallback(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    setFollowupFilter('');
    setTabValue(0);
    setSearch(searchInput.trim());
    setPage(0);
  }, [searchInput]);

  const handleReset = useCallback(() => {
    setSearchInput('');
    setSearch('');
    setStatusFilter('all');
    setSourceFilter('all');
    setStageFilter('all');
    setFollowupFilter('');
    setSortOrder('desc');
    setAssignmentFilter('all');
    setSelectedAssociateFilter(null);
    setFilterAssociateSearchTerm('');
    setTabValue(0);
    setPage(0);
  }, []);

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setRowsPerPage(newLimit);
    setPage(0);
  };

  const handleEdit = (row) => {
    history.push('/app/lead/EditLead', { leadData: row.raw || row });
  };
  const handleDelete = async (row) => {
    setConfirmDialog({
      open: true,
      title: 'Confirm Deletion',
      message: 'Are you sure you want to delete this lead?',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      type: 'error',
      action: async () => {
        try {
          await leadService.deleteLead(row.id);
          refetch();
          showAlert('Lead deleted successfully!', 'Success');
        } catch (error) {
          console.error('Error deleting lead:', error);
          showAlert('Something went wrong while deleting the lead!', 'Error');
        }
      }
    });
  };
  const handleViewDetails = (lead) => {
    setSelectedLead(lead);
    setDetailsDialogOpen(true);
  };
  const handleVisit = (lead) => {
    history.push('/app/siteVisit/AddSiteVisit', {
      leadData: {
        _id: lead.id,
        leadId: lead.id,
        id: lead.id,
        name: lead.name,
        phone: lead.phone,
        whatsapp: lead.whatsapp,
        source: lead.source,
        address: lead.location || lead.address,
      }
    });
  };

  const handleAutoVisit = async (lead) => {
    try {
      const today = new Date();

      let currentUserName = 'Admin User';
      try {
        const user = localStorage.getItem('user');
        if (user) {
          const userData = JSON.parse(user);
          currentUserName = userData?.user?.name || userData?.name || userData?.username || 'Admin User';
        }
      } catch (err) { }

      const actualCustomerId = lead.customerId?._id || lead.customerId?.id || lead.customerId;

      const payload = {
        customer: lead.name || 'Unknown',
        mobileNumber: (lead.phone && lead.phone !== '-') ? lead.phone : '0000000000',
        whatsappNumber: lead.whatsapp || '',
        address: lead.location || lead.address || '',
        source: lead.source || '',
        interestedIn: lead.interestedIn === '-' ? '' : lead.interestedIn,
        budgetUpto: lead.budget === '-' ? '' : lead.budget,
        visitDate: today.toISOString(),
        visitTime: today.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }),
        projects: [],
        location: lead.city === '-' ? '' : lead.city,
        siteVisitBy: (lead.assignedTo && lead.assignedTo !== '-') ? lead.assignedTo : currentUserName,
        siteVisitByPhone: (lead.assignedMobile && lead.assignedMobile !== '-') ? lead.assignedMobile : '',
        inquiryStatus: 'READY TO VISIT',
        nextFollowUpDate: today.toISOString(),
        followUpRemark: 'Automatically recorded visit from Lead List',
        leadId: lead.id,
        leadNo: lead.leadNo || lead.leadId,
        createdBy: currentUserName
      };

      if (actualCustomerId) payload.customerId = actualCustomerId;
      if (lead.associateId) payload.siteVisitByAssociateId = lead.associateId;

      const response = await siteVisitService.createSiteVisit(payload);

      // Also update the lead status to "Visited"
      const BASE_URL = process.env.REACT_APP_API_BASE_URL;
      await fetch(`${BASE_URL}/api/leads/${lead.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: 'Visited' }),
      });

      if (response.success || response.status === true) {
        showAlert('Site visit recorded and lead status updated!', 'Success');
        refetch();
        // Redirect removed to keep user on the Lead List
      } else {
        throw new Error(response.message || 'Failed to record visit');
      }
    } catch (err) {
      showAlert('Error recording visit: ' + err.message, 'Error');
    }
  };
  const handleBooking = (lead) => {
    // Extract customer ID accurately from either the ID field or the object
    const actualCustomerId = lead.customerId?._id || lead.customerId?.id || lead.customerId;

    // Construct associate data from what we have in lead (from mapLead)
    const associateData = lead.associateId ? {
      id: lead.associateId,
      name: lead.assignedTo !== '-' ? lead.assignedTo : "",
      phone: lead.assignedMobile !== '-' ? lead.assignedMobile : ""
    } : null;

    history.push('/app/booking/AddBooking', {
      leadData: {
        ...lead,
        name: lead.name === '-' ? '' : lead.name,
        phone: lead.phone === '-' ? '' : lead.phone,
        customerId: actualCustomerId
      },
      visitId: null,
      associateData: associateData
    });
  };

  const handleUpdateStatus = async (lead, newStatus) => {
    try {
      const BASE_URL = process.env.REACT_APP_API_BASE_URL;
      const res = await fetch(`${BASE_URL}/api/leads/${lead.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) throw new Error('Failed to update status');
      showAlert(`Status updated to ${newStatus}`, 'Success');
      refetch();
    } catch (err) {
      showAlert('Error updating status: ' + err.message, 'Error');
    }
  };

  const handleTransferAssociate = (lead) => {
    setTransferLead(lead);
    setSelectedAssociate(null);
    setTransferRemark('');
    setTransferDialogOpen(true);
  };
  const handleCustomerHistory = (lead) => {
    history.push('/app/members/customer/history', {
      openPurchaseHistoryFor: {
        customerId: lead.leadId,
        phone: lead.phone,
        email: lead.email,
        name: lead.name,
      },
    });
  };
  const handleConfirmTransfer = async () => {
    if (!transferLead || !selectedAssociate) return;
    try {
      const BASE_URL = process.env.REACT_APP_API_BASE_URL;
      const payload = {
        leadId: transferLead.id,
        associateId: selectedAssociate.id,
        newAssociate: {
          name: selectedAssociate.name,
          phone: selectedAssociate.mobile,
        },
      };
      const res = await fetch(`${BASE_URL}/api/leads/transfer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Transfer failed');

      refetch();

      setTimelineData(prev => [{
        id: Date.now(),
        date: new Date().toLocaleString(),
        activity: 'Associate Transferred',
        description: `Transferred to ${selectedAssociate.name}${transferRemark ? ' — ' + transferRemark : ''}`,
        status: 'Updated',
        performedBy: selectedAssociate.name,
      }, ...prev]);
      showAlert('Lead transferred successfully', 'Success');
      setTransferDialogOpen(false);
      setTransferLead(null);
      setSelectedAssociate(null);
      setTransferRemark('');
    } catch (err) {
      showAlert('Failed to transfer associate: ' + err.message, 'Error');
    }
  };
  const handleFollowUp = async (lead) => {
    setSelectedLead(lead);
    setFollowUpPickerOpen(false);
    setNextFollowUpPickerOpen(false);
    setFollowUpDialogOpen(true);
    const now = new Date().toISOString();
    setFollowUpData({
      followUpDate: now,
      nextFollowUpDate: '',
      inquiryStatus: '',
      budgetUpto: lead.budget || '',
      remark: '',
    });
    setFollowUpError('');
    setFollowUpSuccess('');
    setFollowUpLoading(true);
    setFollowUpSaving(false);
    setFollowUpHistory([]);
    try {
      // Fetch inquiry statuses to ensure dropdown is populated with latest data
      const statuses = await getInquiryStatuses();
      if (Array.isArray(statuses)) {
        // Filter by stageType 'Lead' and handle unique statuses
        const leadStatuses = statuses.filter(item => (item.stageType || '').toLowerCase() === 'lead');
        const uniqueStatuses = Array.from(new Map(leadStatuses.map(item => [(item.statusName || '').trim().toUpperCase(), item])).values());
        setInquiryStatuses(uniqueStatuses);
      }

      const BASE_URL = process.env.REACT_APP_API_BASE_URL;
      const res = await fetch(`${BASE_URL}/api/leads/timeline/${lead.id}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      const apiHistory = Array.isArray(json?.data) ? json.data : [];
      const formatted = apiHistory.map((f) => ({
        id: f._id || Date.now(),
        type: f.type || '-',
        followUpDate: f.date ? new Date(f.date).toLocaleDateString() : '-',
        remark: f.remark || '-',
        nextScheduleDate: f.nextFollowUpDate
          ? new Date(f.nextFollowUpDate).toLocaleDateString()
          : '-',
        status: f.status || 'Pending',
        createdBy: f.createdBy || '-',
      }));
      setFollowUpHistory(formatted);
    } catch (err) {
      console.error('Failed to load follow-up timeline:', err);
    } finally {
      setFollowUpLoading(false);
    }
  };
  const handleFollowUpDialogClose = () => {
    setFollowUpDialogOpen(false);
    setFollowUpPickerOpen(false);
    setNextFollowUpPickerOpen(false);
  };
  const handleFollowUpFormChange = (field, value) => {
    setFollowUpData((prev) => ({ ...prev, [field]: value }));
  };
  const handleFollowUpSave = async () => {
    const { followUpDate, nextFollowUpDate, inquiryStatus, remark, budgetUpto } = followUpData;
    if (!followUpDate || !nextFollowUpDate || !inquiryStatus || !remark) {
      setFollowUpError('Please fill all required fields.');
      return;
    }
    const payload = {
      leadId: selectedLead.id,
      leadNo: selectedLead.leadNo || '',
      type: inquiryStatus,
      date: followUpDate,
      nextFollowUpDate: nextFollowUpDate,
      remark: remark,
      budgetUpto: budgetUpto,
      doneBy: 'Current User',
      createdBy: 'admin',
    };
    try {
      setFollowUpError('');
      setFollowUpSuccess('');
      setFollowUpSaving(true);
      const BASE_URL = process.env.REACT_APP_API_BASE_URL;
      const res = await fetch(`${BASE_URL}/api/leads/followup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || `HTTP ${res.status}`);
      }
      const result = await res.json();
      const newEntry = {
        id: Date.now(),
        type: inquiryStatus,
        followUpDate: new Date(followUpDate).toLocaleDateString(),
        remark,
        nextScheduleDate: new Date(nextFollowUpDate).toLocaleDateString(),
        status: 'Pending',
        createdBy: 'admin',
      };
      setFollowUpHistory((prev) => [newEntry, ...prev]);
      setFollowUpSuccess('Follow-up saved successfully!');
      setFollowUpData({
        followUpDate: new Date().toISOString(),
        nextFollowUpDate: '',
        inquiryStatus: '',
        budgetUpto: selectedLead?.budget || '',
        remark: '',
      });
      refetch();
    } catch (err) {
      setFollowUpError(err.message || 'Failed to save follow-up');
    } finally {
      setFollowUpSaving(false);
    }
  };
  const handleTimeline = async (lead) => {
    setSelectedLead(lead);
    setTimelineDialogOpen(true);
    setTimelineData([]);
    try {
      const BASE_URL = process.env.REACT_APP_API_BASE_URL;
      const res = await fetch(`${BASE_URL}/api/leads/timeline/${lead.id}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      const apiTimeline = Array.isArray(json?.data) ? json.data : [];
      const formatted = apiTimeline.map((item, idx) => ({
        id: item._id || Date.now() + idx,
        date: item.date ? new Date(item.date).toLocaleString() : '-',
        activity: item.type || item.activity || '-',
        description: item.remark || item.description || '-',
        status: item.status || 'Created',
        performedBy: item.createdBy || item.performedBy || '-',
      }));
      setTimelineData(formatted);
    } catch (err) {
      console.error('Failed to load timeline:', err);
    }
  };
  const handleCloseTimeline = () => setTimelineDialogOpen(false);
  const handleCardTabChange = (leadId, newValue) => {
    setCardTabValue((prev) => ({ ...prev, [leadId]: newValue }));
  };
  const getCardTabValue = (leadId) => cardTabValue[leadId] || 0;
  const getAvatarInitials = (name) => {
    const parts = (name || '').trim().split(' ');
    if (parts.length === 1) return (name?.charAt(0) || '').toUpperCase() + (name?.charAt(1) || '').toUpperCase();
    const first = parts[0] || '';
    const last = parts[parts.length - 1] || '';
    return first.charAt(0).toUpperCase() + last.charAt(0).toUpperCase();
  };
  const getAvatarColor = (name) => {
    const colors = ['#1976d2', '#d32f2f', '#388e3c', '#f57c00', '#7b1fa2', '#0097a7', '#c2185b', '#5d4037', '#455a64', '#00796b'];
    return colors[(name?.charCodeAt(0) || 0) % colors.length];
  };

  const handleSelectLead = (lead) => {
    setSelectedLeads(prev => {
      const isSelected = prev.some(selected => selected.id === lead.id);
      if (isSelected) {
        return prev.filter(selected => selected.id !== lead.id);
      } else {
        return [...prev, lead];
      }
    });
  };

  const handleSelectAllLeads = () => {
    if (selectedLeads.length === data.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads([...data]);
    }
  };

  const loadFilterAssociates = useCallback(async (search = '') => {
    setLoadingFilterAssociates(true);
    try {
      const response = await getAssociates({ search, limit: 10 });
      const associates = Array.isArray(response?.data)
        ? response.data
        : (response?.data?.docs || response?.docs || []);

      setFilterAssociateOptions(associates);
    } catch (error) {
      console.error('Failed to load filter associates:', error);
      setFilterAssociateOptions([]);
    } finally {
      setLoadingFilterAssociates(false);
    }
  }, []);

  // Debounced search for filter associates
  useEffect(() => {
    const timer = setTimeout(() => {
      loadFilterAssociates(filterAssociateSearchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [filterAssociateSearchTerm, loadFilterAssociates]);

  const loadAssignees = useCallback(async (search = '') => {
    setLoadingAssignees(true);
    try {
      const response = await getAssociates({ search, limit: 10 });
      const associates = Array.isArray(response?.data)
        ? response.data
        : (response?.data?.docs || response?.docs || []);

      // Format assignees for the dropdown
      const formattedAssignees = associates.map(associate => ({
        id: associate._id,
        name: associate.name || '',
        email: associate.email || '',
        role: associate.designation || associate.role || 'Associate',
      }));

      setAssigneeDirectory(formattedAssignees);
    } catch (error) {
      console.error('Failed to load assignees:', error);
      setAssigneeDirectory([]);
    } finally {
      setLoadingAssignees(false);
    }
  }, []);

  // Debounced search for assignees
  useEffect(() => {
    const timer = setTimeout(() => {
      if (assignDialogOpen) {
        loadAssignees(assigneeSearchTerm);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [assigneeSearchTerm, assignDialogOpen]);

  useEffect(() => {
    const fetchBudgetsData = async () => {
      const [budgets, sourcesData, inquiryStatusesData] = await Promise.all([
        getBudgets(),
        getSources(),
        getInquiryStatuses()
      ]);
      setBudgetOptions(budgets);
      setSources(sourcesData);

      // Filter by stageType 'Lead' and normalize
      const leadStatuses = (inquiryStatusesData || []).filter(item => (item.stageType || '').toLowerCase() === 'lead');
      const uniqueStatuses = Array.from(new Map(leadStatuses.map(item => [(item.statusName || '').trim().toUpperCase(), item])).values());
      setInquiryStatuses(uniqueStatuses);

      // Create a map of status name to color code from API
      // Prioritize Lead stage statuses if duplicate names exist across stages
      const colorMap = {};
      (inquiryStatusesData || []).forEach(s => {
        const name = (s.statusName || '').trim().toUpperCase();
        if (name) {
          const isLead = (s.stageType || '').toLowerCase() === 'lead';
          // If not already in map, or if this is a 'Lead' stage status, update/set it
          if (!colorMap[name] || isLead) {
            colorMap[name] = s.colorCode;
          }
        }
      });
      setStatusColors(colorMap);
    };
    fetchBudgetsData();
  }, []);

  const handleOpenAssignDialog = () => {
    if (selectedLeads.length === 0) {
      showAlert('Please select at least one lead to assign.', 'Warning');
      return;
    }
    setAssignDialogOpen(true);
    // Load assignees when dialog opens
    loadAssignees();
  };

  const handleCloseAssignDialog = () => {
    setAssignDialogOpen(false);
    setAssignRemark('');
    setSelectedAssignee(null);
    setAssigneeSearchTerm('');
  };

  const handleBulkAssign = async () => {
    if (selectedLeads.length === 0 || !selectedAssignee) return;

    setAssignLoading(true);
    try {
      // Make API calls for each selected lead
      const assignPromises = selectedLeads.map(lead =>
        leadService.changeAssociate({
          leadId: lead.id,
          associateId: selectedAssignee.id,
          remark: assignRemark || 'Bulk assignment',
        })
      );

      await Promise.all(assignPromises);

      // Reset form state
      setSelectedLeads([]);
      setAssignDialogOpen(false);
      setAssignRemark('');
      setSelectedAssignee(null);
      showAlert(`Successfully assigned ${selectedLeads.length} lead(s) to ${selectedAssignee.name}`, 'Success');

      // Refresh the leads list
      refetch();
    } catch (error) {
      console.error('Error assigning leads:', error);
      showAlert('Error assigning leads. Please try again.', 'Error');
    } finally {
      setAssignLoading(false);
    }
  };
  const getPriorityColor = (priority) => {
    if (!priority) return '#9e9e9e';
    const normalizedPriority = priority.trim().toUpperCase();
    
    // First check in the dynamic status colors from API
    if (statusColors[normalizedPriority]) {
      return statusColors[normalizedPriority];
    }
    
    // Fallback to existing hardcoded colors for backward compatibility
    switch (priority) {
      case 'High':
      case 'Hot': return '#f44336';
      case 'Medium':
      case 'Warm': return '#ff9800';
      case 'Low':
      case 'Cold': return '#4caf50';
      default: return '#9e9e9e';
    }
  };

  const getStatusColor = (status) => {
    const s = (status || '').toUpperCase();

    // Check if dynamic color exists
    if (statusColors[s]) {
      return { backgroundColor: statusColors[s], color: '#fff' };
    }

    switch (s) {
      case 'NEW':
      case 'CREATED': return { backgroundColor: '#1976d2', color: '#fff' };
      case 'CONTACTED': return { backgroundColor: '#f57c00', color: '#fff' };
      case 'QUALIFIED': return { backgroundColor: '#2e7d32', color: '#fff' };
      case 'CONVERTED': return { backgroundColor: '#7b1fa2', color: '#fff' };
      case 'HOT': return { backgroundColor: '#d32f2f', color: '#fff' };
      case 'WARM': return { backgroundColor: '#ed6c02', color: '#fff' };
      case 'COLD': return { backgroundColor: '#0288d1', color: '#fff' };
      case 'INTERESTED': return { backgroundColor: '#2e7d32', color: '#fff' };
      case 'NOT INTERESTED': return { backgroundColor: '#455a64', color: '#fff' };
      case 'UPDATED': return { backgroundColor: '#ab47bc', color: '#fff' };
      case 'SCHEDULED': return { backgroundColor: '#29b6f6', color: '#fff' };
      case 'IN PROGRESS': return { backgroundColor: '#ffa726', color: '#fff' };
      case 'READY TO VISIT': return { backgroundColor: '#4caf50', color: '#fff' };
      default: return { backgroundColor: '#9e9e9e', color: '#fff' };
    }
  };

  if (!canReadLead) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '70vh', p: 3 }}>
        <Paper elevation={3} sx={{ p: { xs: 3, md: 6 }, textAlign: 'center', maxWidth: 500, borderRadius: 3, boxShadow: '0 10px 40px rgba(0,0,0,0.1)' }}>
          <Avatar sx={{ bgcolor: '#fee2e2', color: '#ef4444', width: 80, height: 80, mb: 3, mx: 'auto' }}>
            <WarningIcon sx={{ fontSize: 50 }} />
          </Avatar>
          <Typography variant="h4" fontWeight="bold" color="#1a202c" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, fontSize: '1.1rem' }}>
            You do not have permission to view Lead List. Please contact your administrator for access.
          </Typography>
          <Button
            variant="contained"
            onClick={() => history.push('/app/dashboard')}
            sx={{
              backgroundColor: '#536DFE',
              '&:hover': { backgroundColor: '#3d5afe' },
              px: 4, py: 1.2, borderRadius: 2, fontWeight: 600, textTransform: 'none'
            }}
          >
            Back to Dashboard
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, backgroundColor: '#f9f9fb', minHeight: '100vh' }}>
      <Box sx={{ mb: 4 }}>
        <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
              Lead Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage and track all your leads
            </Typography>
          </Box>
          <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={1} width={{ xs: '100%', sm: 'auto' }} flexWrap="wrap" alignItems="center">
            {canWriteLead && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleOpenAssignDialog}
                startIcon={<PersonIcon />}
                sx={{
                  fontWeight: 600,
                  px: 2,
                  textTransform: 'none',
                  fontSize: '10px',
                  borderRadius: 2,
                  width: { xs: '100%', sm: 'auto' },
                  height: { xs: 'auto', sm: 36 }
                }}
              >
                Assign To {selectedLeads.length > 0 ? `(${selectedLeads.length})` : ''}
              </Button>
            )}
            {canWriteLead && (
              <Button
                variant="outlined"
                color="primary"
                onClick={() => history.push('/app/members/leads/bulk-import')}
                startIcon={<CloudUploadIcon />}
                sx={{
                  fontWeight: 600,
                  px: 2,
                  textTransform: 'none',
                  fontSize: '12px',
                  borderRadius: 2,
                  width: { xs: '100%', sm: 'auto' },
                  height: { xs: 'auto', sm: 36 }
                }}
              >
                Bulk Import
              </Button>
            )}
            {canWriteLead && (
              <ExportButton
                onExportPdf={handleExportPdf}
                onExportCsv={handleExportCsv}
                onExportExcel={handleExportExcel}
                variant="outlined"
                sx={{
                  fontSize: '12px',
                  height: { xs: 'auto', sm: 36 },
                  width: { xs: '100%', sm: 'auto' },
                  px: 2
                }}
              />
            )}
            {canWriteLead && (
              <Button
                variant="contained"
                color="primary"
                onClick={() => history.push('/app/lead/AddLead')}
                startIcon={<AddIcon />}
                sx={{
                  fontWeight: 600,
                  px: 3,
                  textTransform: 'none',
                  fontSize: '11px',
                  boxShadow: '0 4px 12px rgba(83, 109, 254, 0.3)',
                  borderRadius: 2,
                  width: { xs: '100%', sm: 'auto' },
                  height: { xs: 'auto', sm: 36 }
                }}
              >
                Add New Lead
              </Button>
            )}
            <RefreshButton
              onClick={refetch}
              loading={loading}
              sx={{
                fontWeight: 600,
                px: 2,
                fontSize: '12px',
                borderRadius: 2,
                width: { xs: '100%', sm: 'auto' },
                height: { xs: 'auto', sm: 36 }
              }}
            />
          </Box>
        </Box>
      </Box>
      <Card sx={{ mb: 2, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', overflow: 'visible' }}>
        <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
          <Grid container spacing={1.5} alignItems="center">
            {/* Search and Toggle Row */}
            <Grid item xs={12} md={showMobileFilters || !isMobile ? 4 : 12}>
              <Box display="flex" flexDirection="column">
                <Box display="flex" gap={1}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Search Leads"
                    placeholder="Name, phone or ID..."
                    value={searchInput}
                    onChange={handleSearchInputChange}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleSearchClick();
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon color="primary" sx={{ fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        height: '44px',
                        backgroundColor: '#fff',
                        '& fieldset': { borderColor: '#e0e0e0' },
                        '&:hover fieldset': { borderColor: '#536DFE' },
                        '&.Mui-focused fieldset': { borderColor: '#536DFE' },
                      },
                      '& .MuiOutlinedInput-input': {
                        fontSize: '13px',
                      }
                    }}
                  />
                  {isMobile && (
                    <Button
                      variant={showMobileFilters ? "contained" : "outlined"}
                      onClick={() => setShowMobileFilters(!showMobileFilters)}
                      sx={{
                        minWidth: '44px',
                        height: '44px',
                        borderRadius: 2,
                        borderColor: '#e0e0e0',
                        p: 0,
                        backgroundColor: showMobileFilters ? '#536DFE' : 'transparent',
                        color: showMobileFilters ? '#fff' : '#536DFE',
                        '&:hover': {
                          backgroundColor: showMobileFilters ? '#3D5AFE' : 'rgba(83, 109, 254, 0.04)',
                        }
                      }}
                    >
                      <TuneIcon />
                    </Button>
                  )}
                </Box>
              </Box>
            </Grid>

            {/* Filters Section */}
            <Grid item xs={12} md={8} sx={{ display: { xs: showMobileFilters ? 'block' : 'none', md: 'block' } }}>
              <Grid container spacing={1.5} alignItems="center">
                <Grid item xs={12} sm={6} md={6}>
                  <Autocomplete
                    fullWidth
                    size="small"
                    options={filterAssociateOptions}
                    getOptionLabel={(option) => option.name || ''}
                    value={selectedAssociateFilter}
                    onChange={(event, newValue) => {
                      setSelectedAssociateFilter(newValue);
                      setPage(0);
                    }}
                    onInputChange={(event, newInputValue) => {
                      setFilterAssociateSearchTerm(newInputValue);
                    }}
                    loading={loadingFilterAssociates}
                    renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Associate Search"
                          placeholder="Search Associate..."
                          variant="outlined"
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position="start">
                                <PersonIcon color="primary" sx={{ fontSize: 20 }} />
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          ),
                          endAdornment: (
                            <>
                              {loadingFilterAssociates ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              height: '44px',
                              backgroundColor: '#fff',
                              '& fieldset': { borderColor: '#e0e0e0' },
                              '&:hover fieldset': { borderColor: '#536DFE' },
                              '& .MuiAutocomplete-input': {
                                fontSize: '13px',
                              }
                            }
                          }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={3} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel sx={{ fontSize: '12px' }}>Status</InputLabel>
                    <Select
                      value={statusFilter}
                      label="Status"
                      onChange={(e) => {
                        const val = e.target.value;
                        setStatusFilter(val);
                        setVisitTypeFilter('');
                        setTabValue(0);
                        setPage(0);
                      }}
                      sx={{
                        borderRadius: 2,
                        height: '44px',
                        backgroundColor: '#fff',
                        '& .MuiSelect-select': { fontSize: '13px' },
                        '& fieldset': { borderColor: '#e0e0e0' },
                        '&:hover fieldset': { borderColor: '#536DFE' },
                      }}
                    >
                      <MenuItem value="all" sx={{ fontSize: '13px' }}>All Status</MenuItem>
                      {inquiryStatuses.map((p) => (
                        <MenuItem key={p.id} value={p.statusName} sx={{ fontSize: '13px' }}>
                          {p.statusName}
                        </MenuItem>
                      ))}
                      {!inquiryStatuses.some(p => (p.statusName || '').trim().toUpperCase() === 'HOT') && <MenuItem value="HOT" sx={{ fontSize: '13px' }}>HOT</MenuItem>}
                      {!inquiryStatuses.some(p => (p.statusName || '').trim().toUpperCase() === 'WARM') && <MenuItem value="WARM" sx={{ fontSize: '13px' }}>WARM</MenuItem>}
                      {!inquiryStatuses.some(p => (p.statusName || '').trim().toUpperCase() === 'COLD') && <MenuItem value="COLD" sx={{ fontSize: '13px' }}>COLD</MenuItem>}
                      {!inquiryStatuses.some(p => p.statusName.toUpperCase() === 'READY TO VISIT') && <MenuItem value="Ready to visit" sx={{ fontSize: '13px' }}>Ready to visit</MenuItem>}
                      {!inquiryStatuses.some(p => p.statusName.toUpperCase() === 'VISITED') && <MenuItem value="Visited" sx={{ fontSize: '13px' }}>Visited</MenuItem>}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={3} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel sx={{ fontSize: '12px' }}>Source</InputLabel>
                    <Select
                      value={sourceFilter}
                      label="Source"
                      onChange={(e) => {
                        setSourceFilter(e.target.value);
                        setPage(0);
                      }}
                      sx={{
                        borderRadius: 2,
                        height: '44px',
                        backgroundColor: '#fff',
                        '& .MuiSelect-select': { fontSize: '13px' },
                        '& fieldset': { borderColor: '#e0e0e0' },
                        '&:hover fieldset': { borderColor: '#536DFE' },
                      }}
                    >
                      <MenuItem value="all" sx={{ fontSize: '13px' }}>All Sources</MenuItem>
                      {sources.map((s) => (
                        <MenuItem key={s.id} value={s.name} sx={{ fontSize: '13px' }}>
                          {s.name}
                        </MenuItem>
                      ))}
                      {!sources.some(s => s.name === 'Website') && <MenuItem value="Website" sx={{ fontSize: '13px' }}>Website</MenuItem>}
                      {!sources.some(s => s.name === 'Walk-in') && <MenuItem value="Walk-in" sx={{ fontSize: '13px' }}>Walk-in</MenuItem>}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Grid>

            {/* Actions Row */}
            <Grid item xs={12} sx={{ display: { xs: showMobileFilters ? 'block' : 'none', md: 'block' } }}>
              <Grid container spacing={1.5} alignItems="center">
                <Grid item xs={12} sm={4} md={2.5}>
                  <FormControl fullWidth size="small">
                    <InputLabel sx={{ fontSize: '12px' }}>Assignment</InputLabel>
                    <Select
                      value={assignmentFilter}
                      label="Assignment"
                      onChange={(e) => {
                        setAssignmentFilter(e.target.value);
                        setPage(0);
                      }}
                      sx={{
                        borderRadius: 2,
                        height: '44px',
                        backgroundColor: '#fff',
                        '& .MuiSelect-select': { fontSize: '13px' },
                        '& fieldset': { borderColor: '#e0e0e0' },
                        '&:hover fieldset': { borderColor: '#536DFE' },
                      }}
                    >
                      <MenuItem value="all" sx={{ fontSize: '13px' }}>All</MenuItem>
                      <MenuItem value="assigned" sx={{ fontSize: '13px' }}>Assigned</MenuItem>
                      <MenuItem value="unassigned" sx={{ fontSize: '13px' }}>Unassigned</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={4} md={1.5}>
                  <FormControl fullWidth size="small">
                    <InputLabel sx={{ fontSize: '12px' }}>Sort</InputLabel>
                    <Select
                      value={sortOrder}
                      label="Sort"
                      onChange={(e) => {
                        setSortOrder(e.target.value);
                        setPage(0);
                      }}
                      sx={{
                        borderRadius: 2,
                        height: '44px',
                        backgroundColor: '#fff',
                        '& .MuiSelect-select': { fontSize: '13px' },
                        '& fieldset': { borderColor: '#e0e0e0' },
                        '&:hover fieldset': { borderColor: '#536DFE' },
                      }}
                    >
                      <MenuItem value="all" sx={{ fontSize: '13px' }}>All</MenuItem>
                      <MenuItem value="desc" sx={{ fontSize: '13px' }}>Desc</MenuItem>
                      <MenuItem value="asc" sx={{ fontSize: '13px' }}>Asc</MenuItem>
                      
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={4} md={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSearchClick}
                    fullWidth
                    sx={{
                      height: '44px',
                      mt: { md: 0, xs: 0 }, // No margin needed since we removed external labels
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '14px',
                      borderRadius: 2,
                      backgroundColor: '#536DFE',
                      boxShadow: '0 4px 10px rgba(83, 109, 254, 0.25)',
                      '&:hover': {
                        backgroundColor: '#3D5AFE',
                        boxShadow: '0 6px 14px rgba(83, 109, 254, 0.35)',
                      }
                    }}
                  >
                    Apply Filters
                  </Button>
                </Grid>

                <Grid item xs={12} sm={6} md={1.5}>
                  <ResetButton onClick={handleReset} />
                </Grid>

                {canWriteLead && (
                  <Grid item xs={12} sm={6} md={2}>
                    <Box display="flex" alignItems="center" justifyContent="flex-end" height="44px" sx={{ pr: 1, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedLeads.length === data.length && data.length > 0}
                            indeterminate={selectedLeads.length > 0 && selectedLeads.length < data.length}
                            onChange={handleSelectAllLeads}
                            color="primary"
                            size="small"
                          />
                        }
                        label="Assign All Leads"
                        sx={{
                          margin: 0,
                          '& .MuiTypography-root': {
                            fontSize: '13px',
                            fontWeight: 700,
                            color: '#536DFE',
                            whiteSpace: 'nowrap'
                          }
                        }}
                      />
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      <Box sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => {
            setTabValue(newValue);
            const filterMap = ['', 'today', 'fresh', 'upcoming', 'pending'];
            setFollowupFilter(filterMap[newValue] || '');
            setPage(0);
          }}
          sx={{
            backgroundColor: 'white',
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '13px', minHeight: 56 },
          }}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          <Tab label={`All Leads (${tabValue === 0 ? totalDocs : totalDocs})`} />
          <Tab label="Today Follow Up" />
          <Tab label="Fresh Leads" />
          <Tab label="Upcoming Follow Up" />
          <Tab label="Pending Follow Up" />
        </Tabs>
      </Box>
      <DataStateHandler
        loading={loading}
        loadingMessage="Fetching leads..."
        error={error}
        onRetry={refetch}
        isEmpty={!loading && !error && data.length === 0}
        emptyTitle="No leads found"
        emptyMessage="Try adjusting your search or filters to discover more leads."
        emptyActionLabel={canWriteLead ? "Add New Lead" : undefined}
        onEmptyAction={canWriteLead ? () => history.push('/app/lead/AddLead') : undefined}
        renderLoading={() => (
          <LeadCardSkeleton count={Math.min(rowsPerPage, 6)} />
        )}
      >
      
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {data.map((lead) => (
              <Grid item xs={12} key={lead.id}>
                <Card
                  sx={{
                    boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
                    borderRadius: 2,
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 6px 16px rgba(0,0,0,0.12)' },
                  }}
                >
                  <Box sx={{
                    p: 1.5,
                    backgroundColor: '#536DFE',
                    color: 'white'
                  }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={3}>
                        <Box display="flex" alignItems="center" gap={1}>
                          {canWriteLead && (
                            <Box sx={{
                              backgroundColor: 'white',
                              borderRadius: '50%',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                              mr: 1,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 32,
                              height: 32,
                            }}>
                              <Checkbox
                                checked={selectedLeads.some(selected => selected.id === lead.id)}
                                onChange={() => handleSelectLead(lead)}
                                color="primary"
                                size="small"
                                sx={{ p: 0 }}
                              />
                            </Box>
                          )}
                          <Typography variant="body2" sx={{ fontSize: '13px', color: 'rgba(255,255,255,0.85)' }}>
                            Lead ID:
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 700, fontSize: '14px' }}>
                            {lead.leadId}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <Box display="flex" alignItems="center" justifyContent={{ xs: 'flex-start', sm: 'center' }} gap={1}>
                          <Typography variant="body2" sx={{ fontSize: '13px', color: 'rgba(255,255,255,0.85)' }}>
                            Next Follow Up:
                          </Typography>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <FollowUpIcon sx={{ fontSize: 14 }} />
                            <Typography variant="body1" sx={{ fontWeight: 700, fontSize: '12px' }}>
                              {lead.nextFollowUp}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <Box display="flex" alignItems="center" justifyContent={{ xs: 'flex-start', sm: 'center' }} gap={1}>
                          <Typography variant="body2" sx={{ fontSize: '13px', color: 'rgba(255,255,255,0.85)' }}>
                            Timeline:
                          </Typography>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <TimelineIcon sx={{ fontSize: 14 }} />
                            <Typography variant="body1" sx={{ fontWeight: 700, fontSize: '12px' }}>
                              {lead.timelineCount}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <Box display="flex" alignItems="center" justifyContent={{ xs: 'flex-start', sm: 'flex-end' }} gap={1}>
                          <Typography variant="body2" sx={{ fontSize: '13px', color: 'rgba(255,255,255,0.85)' }}>
                            Status:
                          </Typography>
                          <Chip
                            label={lead.status}
                            size="small"
                            sx={{
                              backgroundColor: getStatusColor(lead.status).backgroundColor,
                              color: getStatusColor(lead.status).color,
                              fontWeight: 700,
                              fontSize: '11px',
                              height: '24px',
                            }}
                          />
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                  <CardContent sx={{ pt: 2, pb: 1.5, px: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={4}>
                        <Box sx={{ display: 'flex', gap: 2, p: 1 }}>
                          <Box sx={{ flexShrink: 0 }}>
                            <Avatar
                              alt={lead.name}
                              sx={{
                                width: { xs: 60, sm: 100 },
                                height: { xs: 60, sm: 100 },
                                border: '3px solid #e0e0e0',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                backgroundColor: getAvatarColor(lead.name),
                                fontSize: { xs: '1.2rem', sm: '1.6rem' },
                                fontWeight: 700,
                              }}
                            >
                              {getAvatarInitials(lead.name)}
                            </Avatar>
                          </Box>
                          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#000', mb: 0.5, fontSize: { xs: '0.9rem', sm: '1rem' }, lineHeight: 1.3 }}>
                              {lead.name}
                            </Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ fontSize: '12px', mb: 0.3, lineHeight: 1.4 }}>
                              {lead.designation}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '11px', mb: 0.7, lineHeight: 1.4 }}>
                              {lead.company}
                            </Typography>
                            <Box sx={{ mb: 1, display: 'flex', gap: 0.5 }}>
                              <Chip
                                label={lead.priority}
                                sx={{
                                  height: '24px',
                                  backgroundColor: getPriorityColor(lead.priority),
                                  color: 'white',
                                  fontWeight: 600,
                                  fontSize: '10px',
                                }}
                              />
                              <Box display="flex" alignItems="center">
                                {[...Array(5)].map((_, i) => (
                                  <StarIcon
                                    key={i}
                                    sx={{
                                      fontSize: 14,
                                      color: i < (lead.rating || 0) ? '#FFC107' : '#E0E0E0',
                                    }}
                                  />
                                ))}
                              </Box>
                            </Box>
                            <Divider sx={{ my: 1 }} />
                            <Box>
                              <Box display="flex" alignItems="center" gap={1} sx={{ mb: 0.4 }}>
                                <EmailIcon sx={{ fontSize: 14, color: '#666' }} />
                                <Typography variant="body1" sx={{ fontSize: '11px', lineHeight: 1.5, color: '#000' }}>
                                  {lead.email}
                                </Typography>
                              </Box>
                              <Box display="flex" alignItems="center" gap={1} sx={{ mb: 0.4 }}>
                                <PhoneIcon sx={{ fontSize: 14, color: '#666' }} />
                                <Typography variant="body1" sx={{ fontSize: '11px', lineHeight: 1.5, color: '#000' }}>
                                  {lead.phone}
                                </Typography>
                              </Box>
                              <Box display="flex" alignItems="center" gap={1}>
                                <LocationOnIcon sx={{ fontSize: 14, color: '#666' }} />
                                <Typography variant="body1" sx={{ fontSize: '11px', lineHeight: 1.5, color: '#000' }}>
                                  {lead.city}, {lead.state}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Box>
                          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            <Tabs
                              value={getCardTabValue(lead.id + '_col2')}
                              onChange={(e, v) => handleCardTabChange(lead.id + '_col2', v)}
                              variant="fullWidth"
                              sx={{ minHeight: 42, '& .MuiTab-root': { minHeight: 42, py: 1, textTransform: 'none', fontSize: '11px', fontWeight: 600 } }}
                            >
                              <Tab label="Source" />
                              <Tab label="Assigned" />
                            </Tabs>
                          </Box>
                          <Box sx={{ mt: 1.5, minHeight: 140 }}>
                            {getCardTabValue(lead.id + '_col2') === 0 && (
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 700, mb: 1, color: '#536DFE', fontSize: '12px' }}>
                                  Source Details
                                </Typography>
                                <Typography variant="body1" display="block" sx={{ mb: 0.6, fontSize: '11px', lineHeight: 1.6 }}>
                                  <strong>Source:</strong> {lead.source}
                                </Typography>
                                <Typography variant="body1" display="block" sx={{ mb: 0.6, fontSize: '11px', lineHeight: 1.6 }}>
                                  <strong>Industry:</strong> {lead.industry || '-'}
                                </Typography>
                                <Typography variant="body1" display="block" sx={{ mb: 0.6, fontSize: '11px', lineHeight: 1.6 }}>
                                  <strong>Company Size:</strong> {lead.companySize || '-'}
                                </Typography>
                                <Typography variant="body1" display="block" sx={{ fontSize: '11px', lineHeight: 1.6 }}>
                                  <strong>Website:</strong> {lead.website || '-'}
                                </Typography>
                              </Box>
                            )}
                            {getCardTabValue(lead.id + '_col2') === 1 && (
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 700, mb: 1, color: '#536DFE', fontSize: '12px' }}>
                                  Assigned To
                                </Typography>
                                <Typography variant="body1" display="block" sx={{ mb: 0.6, fontSize: '11px', lineHeight: 1.6 }}>
                                  <strong>Name:</strong> {lead.assignedTo}
                                </Typography>
                                <Typography variant="body1" display="block" sx={{ mb: 0.6, fontSize: '11px', lineHeight: 1.6 }}>
                                  <strong>Email:</strong> {lead.assignedEmail || '-'}
                                </Typography>
                                <Typography variant="body1" display="block" sx={{ fontSize: '11px', lineHeight: 1.6 }}>
                                  <strong>Mobile:</strong> {lead.assignedMobile}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Box>
                          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            <Tabs
                              value={getCardTabValue(lead.id + '_col3')}
                              onChange={(e, v) => handleCardTabChange(lead.id + '_col3', v)}
                              variant="fullWidth"
                              sx={{ minHeight: 42, '& .MuiTab-root': { minHeight: 42, py: 1, textTransform: 'none', fontSize: '11px', fontWeight: 600 } }}
                            >
                              <Tab label="Interest" />
                              <Tab label="Timeline" />
                            </Tabs>
                          </Box>
                          <Box sx={{ mt: 1.5, minHeight: 140 }}>
                            {getCardTabValue(lead.id + '_col3') === 0 && (
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 700, mb: 1, color: '#536DFE', fontSize: '12px' }}>
                                  Interest Details
                                </Typography>
                                <Typography variant="body1" display="block" sx={{ mb: 0.6, fontSize: '11px', lineHeight: 1.6 }}>
                                  <strong>Interested In:</strong> {lead.interestedIn}
                                </Typography>
                                <Typography variant="body1" display="block" sx={{ mb: 0.6, fontSize: '11px', lineHeight: 1.6 }}>
                                  <strong>Budget:</strong> {lead.budget}
                                </Typography>
                                <Typography variant="body1" display="block" sx={{ mb: 0.6, fontSize: '11px', lineHeight: 1.6 }}>
                                  <strong>Location:</strong> {lead.location}
                                </Typography>
                                <Typography variant="body1" display="block" sx={{ fontSize: '11px', lineHeight: 1.6 }}>
                                  <strong>Requirement:</strong> {lead.requirement}
                                </Typography>
                              </Box>
                            )}
                            {getCardTabValue(lead.id + '_col3') === 1 && (
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 700, mb: 1, color: '#536DFE', fontSize: '12px' }}>
                                  Timeline
                                </Typography>
                                <Typography variant="body1" display="block" sx={{ mb: 0.6, fontSize: '11px', lineHeight: 1.6 }}>
                                  <strong>Created:</strong> {lead.createdDate}
                                </Typography>
                                <Typography variant="body1" display="block" sx={{ mb: 0.6, fontSize: '11px', lineHeight: 1.6 }}>
                                  <strong>Last Contact:</strong> {lead.lastContact}
                                </Typography>
                                <Typography variant="body1" display="block" sx={{ fontSize: '11px', lineHeight: 1.6 }}>
                                  <strong>Next Follow-up:</strong> {lead.nextFollowUp}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                  <Box
                    sx={{
                      px: 2,
                      pb: 1.5,
                      pt: 1.5,
                      display: 'flex',
                      gap: 1,
                      borderTop: '1px solid #f0f0f0',
                      flexWrap: 'wrap',
                    }}
                  >
                    {canWriteLead && (
                      <Tooltip title="Edit Lead">
                        <IconButton color="primary" size="small" onClick={() => handleEdit(lead)} sx={{ border: '1px solid #e0e0e0' }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canWriteLead && (
                      <Tooltip title="Delete Lead">
                        <IconButton color="error" size="small" onClick={() => handleDelete(lead)} sx={{ border: '1px solid #e0e0e0' }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canWriteLead && lead.status !== 'Ready to visit' && lead.status !== 'Visited' && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<VisitIcon />}
                        onClick={() => handleUpdateStatus(lead, 'Ready to visit')}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '10px',
                          py: 0.5,
                          flex: 1,
                          minWidth: 100,
                        }}
                      >
                        Ready to visit
                      </Button>
                    )}
                    {canWriteLead && lead.status === 'Ready to visit' && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<VisitIcon />}
                        onClick={() => handleAutoVisit(lead)}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '10px',
                          py: 0.5,
                          flex: 1,
                          minWidth: 100,

                        }}
                      >
                        Visit
                      </Button>
                    )}
                    {canWriteLead && (
                      <Button variant="outlined" size="small" startIcon={<FollowUpIcon />} onClick={() => handleFollowUp(lead)} sx={{ textTransform: 'none', fontWeight: 600, fontSize: '10px', py: 0.5, flex: 1, minWidth: 100 }}>
                        Follow-up
                      </Button>
                    )}
                    {canWriteLead && (
                      <Button variant="outlined" size="small" startIcon={<MoneyIcon />} onClick={() => handleBooking(lead)} sx={{ textTransform: 'none', fontWeight: 600, fontSize: '10px', py: 0.5, flex: 1, minWidth: 100 }}>
                        Booking
                      </Button>
                    )}
                    <Tooltip title="Timeline">
                      <IconButton color="primary" size="small" onClick={() => handleTimeline(lead)} sx={{ border: '1px solid #e0e0e0' }}>
                        <TimelineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Past Bookings">
                      <IconButton color="primary" size="small" onClick={() => handleCustomerHistory(lead)} sx={{ border: '1px solid #e0e0e0' }}>
                        <HistoryIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {/* <Tooltip title="Transfer Associate">
                  <IconButton color="primary" size="small" onClick={() => handleTransferAssociate(lead)} sx={{ border: '1px solid #e0e0e0' }}>
                    <TransferIcon fontSize="small" />
                  </IconButton>
                </Tooltip> */}
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
          {data.length > 0 && (
            <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
              <TablePagination
                
                component="div"
                count={totalDocs}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Card>
          )}
        </>
      </DataStateHandler>
      <Dialog open={detailsDialogOpen} onClose={() => setDetailsDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#536DFE', color: 'white', display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            sx={{
              width: 56,
              height: 56,
              border: '3px solid white',
              backgroundColor: selectedLead ? getAvatarColor(selectedLead.name) : '#1976d2',
              fontSize: '1.25rem',
              fontWeight: 700,
            }}
          >
            {selectedLead ? getAvatarInitials(selectedLead.name) : ''}
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>{selectedLead?.name}</Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>{selectedLead?.leadId} | {selectedLead?.designation}</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={selectedLead ? getCardTabValue(selectedLead.id + '_detail') : 0}
              onChange={(e, v) => selectedLead && handleCardTabChange(selectedLead.id + '_detail', v)}
              variant="fullWidth"
              sx={{ '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '12px' } }}
            >
              <Tab label="Source" />
              <Tab label="Assigned" />
              <Tab label="Interest" />
              <Tab label="Timeline" />
            </Tabs>
          </Box>
          <Box sx={{ p: 3 }}>
            {selectedLead && (
              <>
                {getCardTabValue(selectedLead.id + '_detail') === 0 && (
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: '#000', fontSize: '12px' }}>SOURCE & CONTACT INFO</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary">Lead Source</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>{selectedLead.source}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary">Industry / Profession</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>{selectedLead.industry}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary">Full Name</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>{selectedLead.name}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary">Email</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>{selectedLead.email}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary">Phone</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>{selectedLead.phone}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary">WhatsApp</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>{selectedLead.whatsapp}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary">Company</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>{selectedLead.company}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary">Designation</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>{selectedLead.designation}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary">Address</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>{selectedLead.address || selectedLead.location}</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                )}
                {getCardTabValue(selectedLead.id + '_detail') === 1 && (
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: '#000', fontSize: '12px' }}>Assignment Information</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary">Assigned To</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>{selectedLead.assignedTo}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary">Assigned Email</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>{selectedLead.assignedEmail || '-'}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary">Assigned Mobile</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>{selectedLead.assignedMobile}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary">Priority</Typography>
                        <Chip
                          label={selectedLead.priority}
                          size="small"
                          sx={{
                            mt: 0.5,
                            fontWeight: 600,
                            backgroundColor: getPriorityColor(selectedLead.priority),
                            color: 'white'
                          }}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                )}
                {getCardTabValue(selectedLead.id + '_detail') === 2 && (
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: '#000', fontSize: '12px' }}>Interest & Budget Details</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary">Interested In</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>{selectedLead.interestedIn}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary">Budget Range</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: '#4caf50' }}>{selectedLead.budget}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary">Preferred Location</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>{selectedLead.location}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary">Next Follow Up</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: '#1976d2' }}>{selectedLead.nextFollowUp}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary">Requirement Details</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {selectedLead.requirement || selectedLead.notes}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                )}
                {getCardTabValue(selectedLead.id + '_detail') === 3 && (
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary">Created Date</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>{selectedLead.createdDate}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary">Last Contact</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>{selectedLead.lastContact}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary">Next Follow-up</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>{selectedLead.nextFollowUp}</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                )}
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
          <Button onClick={() => setDetailsDialogOpen(false)} variant="outlined">Close</Button>
          {canWriteLead && (
            <Button onClick={() => selectedLead && handleEdit(selectedLead)} variant="contained" startIcon={<EditIcon />}>
              Edit Lead
            </Button>
          )}
        </DialogActions>
      </Dialog>
      <Dialog
        open={followUpDialogOpen}
        onClose={handleFollowUpDialogClose}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: { xs: '95vh', md: '90vh' },
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: '#536DFE',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            py: 2,
          }}
        >
          <FollowUpIcon sx={{ fontSize: 24 }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Follow-up for {selectedLead?.name}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {selectedLead?.leadId} | {selectedLead?.company}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {followUpError && <Alert severity="error" sx={{ my: 1 }}>{followUpError}</Alert>}
          {followUpSuccess && <Alert severity="success" sx={{ my: 1 }}>{followUpSuccess}</Alert>}

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Grid container spacing={3} mt={1}>
              <Grid item xs={12} sm={6}>
                <DateTimePicker
                  label="Follow-up Date"
                  value={followUpData.followUpDate ? new Date(followUpData.followUpDate) : null}
                  onChange={(value) => handleFollowUpFormChange('followUpDate', value ? value.toISOString() : '')}
                  minDate={new Date()}
                  open={followUpPickerOpen}
                  onOpen={() => setFollowUpPickerOpen(true)}
                  onClose={() => setFollowUpPickerOpen(false)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      required
                      onClick={(event) => {
                        setFollowUpPickerOpen(true);
                        params?.inputProps?.onClick?.(event);
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DateTimePicker
                  label="Next Follow-up Date"
                  value={followUpData.nextFollowUpDate ? new Date(followUpData.nextFollowUpDate) : null}
                  onChange={(value) => handleFollowUpFormChange('nextFollowUpDate', value ? value.toISOString() : '')}
                  minDate={new Date()}
                  open={nextFollowUpPickerOpen}
                  onOpen={() => setNextFollowUpPickerOpen(true)}
                  onClose={() => setNextFollowUpPickerOpen(false)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      required
                      onClick={(event) => {
                        setNextFollowUpPickerOpen(true);
                        params?.inputProps?.onClick?.(event);
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Inquiry Status</InputLabel>
                  <Select
                    value={followUpData.inquiryStatus}
                    onChange={(e) => handleFollowUpFormChange('inquiryStatus', e.target.value)}
                    label="Inquiry Status"
                  >
                    {inquiryStatuses.map((p) => (
                      <MenuItem key={p.id || p._id} value={p.statusName}>
                        {p.statusName}
                      </MenuItem>
                    ))}
                    {/* Fallback items if API is empty */}
                    {inquiryStatuses.length === 0 && (
                      <>
                        <MenuItem value="Hot">Hot</MenuItem>
                        <MenuItem value="Warm">Warm</MenuItem>
                        <MenuItem value="Cold">Cold</MenuItem>
                      </>
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Budget Range</InputLabel>
                  <Select
                    value={followUpData.budgetUpto}
                    onChange={(e) => handleFollowUpFormChange('budgetUpto', e.target.value)}
                    label="Budget Range"
                  >
                    {budgetOptions.map((option) => (
                      <MenuItem key={option.id} value={option.value}>
                        {option.budgetRange}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Remark"
                  value={followUpData.remark}
                  onChange={(e) => handleFollowUpFormChange('remark', e.target.value)}
                  placeholder="Enter follow-up notes..."
                  required
                />
              </Grid>
            </Grid>
          </LocalizationProvider>

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              onClick={handleFollowUpDialogClose}
              disabled={followUpSaving}
              sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleFollowUpSave}
              disabled={followUpSaving}
              startIcon={followUpSaving ? <CircularProgress size={18} color="inherit" /> : <AddIcon />}
              sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
            >
              {followUpSaving ? 'Saving...' : 'Save Follow-up'}
            </Button>
          </Box>

          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#000' }}>
              Recent Follow-up Details
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {followUpLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <TableContainer component={Paper} sx={{ boxShadow: 2, borderRadius: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 700 }}>S.No</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Follow-up Date</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Remark</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Next Schedule Date</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                      {/* <TableCell sx={{ fontWeight: 700 }}>Created By</TableCell> */}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {followUpHistory.length > 0 ? (
                      followUpHistory.map((f, idx) => (
                        <TableRow key={f.id} hover>
                          <TableCell>{idx + 1}</TableCell>
                          <TableCell>{f.followUpDate}</TableCell>
                          <TableCell sx={{ maxWidth: 200 }}>{f.remark}</TableCell>
                          <TableCell>{f.nextScheduleDate}</TableCell>
                          <TableCell>
                            <Chip
                              label={f.type}
                              size="small"
                              sx={{
                                backgroundColor: getStatusColor(f.type).backgroundColor,
                                color: getStatusColor(f.type).color,
                                fontWeight: 600,
                                fontSize: '10px',
                              }}
                            />
                          </TableCell>
                          {/* <TableCell>{f.createdBy}</TableCell> */}
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">No follow-up records found</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </DialogContent>
      </Dialog>
      <Dialog
        open={timelineDialogOpen}
        onClose={handleCloseTimeline}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' } }}
      >
        <DialogTitle
          sx={{
            bgcolor: '#1976d2',
            color: '#fff',
            fontWeight: 700,
            fontSize: 14,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            py: 1.5,
          }}
        >
          <TimelineIcon sx={{ fontSize: 18 }} />
          Lead Timeline
        </DialogTitle>
        <DialogContent sx={{ p: 2, mt: 1 }}>
          {selectedLead && (
            <Box>
              <Paper elevation={2} sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: '#f8f9fa' }}>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography variant="body2" sx={{ color: '#666', fontWeight: 600, fontSize: '10px', mb: 0.5 }}>
                        Name
                      </Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#000', fontSize: 12 }}>
                        {selectedLead.name}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography variant="body2" sx={{ color: '#666', fontWeight: 600, fontSize: '10px', mb: 0.5 }}>
                        Phone Number
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <PhoneIcon sx={{ fontSize: 12, color: '#1976d2' }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#000', fontSize: 12 }}>
                          {selectedLead.phone}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography variant="body2" sx={{ color: '#666', fontWeight: 600, fontSize: '10px', mb: 0.5 }}>
                        WhatsApp Number
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <WhatsAppIcon sx={{ fontSize: 12, color: '#25D366' }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#000', fontSize: 12 }}>
                          {selectedLead.whatsapp}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography variant="body2" sx={{ color: '#666', fontWeight: 600, fontSize: '10px', mb: 0.5 }}>
                        Email ID
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <EmailIcon sx={{ fontSize: 12, color: '#1976d2' }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#000', fontSize: 12 }}>
                          {selectedLead.email}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography variant="body2" sx={{ color: '#666', fontWeight: 600, fontSize: '10px', mb: 0.5 }}>
                        Created On
                      </Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#000', fontSize: 12 }}>
                        {selectedLead.createdDate}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography variant="body2" sx={{ color: '#666', fontWeight: 600, fontSize: '10px', mb: 0.5 }}>
                        Project Name
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <BusinessIcon sx={{ fontSize: 12, color: '#1976d2' }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#000', fontSize: 12 }}>
                          {selectedLead.interestedIn}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography variant="body2" sx={{ color: '#666', fontWeight: 600, fontSize: '10px', mb: 0.5 }}>
                        Created By
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <PersonIcon sx={{ fontSize: 12, color: '#1976d2' }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#000', fontSize: 12 }}>
                          {selectedLead.assignedTo}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography variant="body2" sx={{ color: '#666', fontWeight: 600, fontSize: '10px', mb: 0.5 }}>
                        Next Follow Up
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <FollowUpIcon sx={{ fontSize: 12, color: '#1976d2' }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#000', fontSize: 12 }}>
                          {selectedLead.nextFollowUp}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography variant="body2" sx={{ color: '#666', fontWeight: 600, fontSize: '10px', mb: 0.5 }}>
                        Assigned To
                      </Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#000', fontSize: 14 }}>
                        {selectedLead.assignedTo}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                  </Grid>
                  <Grid item xs={12}>
                    <Box>
                      <Typography variant="body2" sx={{ color: '#666', fontWeight: 600, fontSize: '10px', mb: 0.5 }}>
                        Address
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <LocationOnIcon sx={{ fontSize: 12, color: '#1976d2' }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#000', fontSize: 12 }}>
                          {selectedLead.location}, {selectedLead.city}, {selectedLead.state}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5, color: '#000', display: 'flex', alignItems: 'center', gap: 1, fontSize: 13 }}>
                  <TimelineIcon sx={{ color: '#1976d2', fontSize: 18 }} />
                  Timeline Details
                </Typography>
                <Divider sx={{ mb: 1.5 }} />

                <TableContainer component={Paper} sx={{ boxShadow: 2, borderRadius: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#1976d2' }}>
                        <TableCell sx={{ fontWeight: 700, color: '#fff', fontSize: 12, py: 1 }}>S.No</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#fff', fontSize: 12, py: 1 }}>Date & Time</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#fff', fontSize: 12, py: 1 }}>Activity</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#fff', fontSize: 12, py: 1 }}>Description</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#fff', fontSize: 12, py: 1 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#fff', fontSize: 12, py: 1 }}>Performed By</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {timelineData.length > 0 ? (
                        timelineData.map((item, index) => (
                          <TableRow
                            key={item.id}
                            hover
                            sx={{ '&:nth-of-type(odd)': { bgcolor: '#f9f9f9' } }}
                          >
                            <TableCell sx={{ fontWeight: 500, fontSize: 12, py: 1 }}>{index + 1}</TableCell>
                            <TableCell sx={{ py: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 12 }}>
                                {item.date}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ py: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2', fontSize: 12 }}>
                                {item.activity}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ py: 1 }}>
                              <Typography variant="body2" sx={{ color: '#666', fontSize: 12 }}>
                                {item.description}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ py: 1 }}>
                              <Chip
                                label={item.status}
                                size="small"
                                sx={{
                                  backgroundColor: getStatusColor(item.status).backgroundColor,
                                  color: getStatusColor(item.status).color,
                                  fontWeight: 600,
                                  fontSize: '10px',
                                  height: 22
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ py: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Avatar
                                  sx={{
                                    width: 24,
                                    height: 24,
                                    fontSize: '10px',
                                    bgcolor: getAvatarColor(item.performedBy)
                                  }}
                                >
                                  {getAvatarInitials(item.performedBy)}
                                </Avatar>
                                <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 12 }}>
                                  {item.performedBy}
                                </Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                            <Typography variant="body2" color="textSecondary" sx={{ fontSize: 12 }}>
                              No timeline data available
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0', bgcolor: '#f8f9fa' }}>
          <Button
            onClick={handleCloseTimeline}
            variant="contained"
            sx={{
              textTransform: 'none',
              px: 3,
              fontWeight: 600
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={transferDialogOpen}
        onClose={() => {
          setTransferDialogOpen(false);
          setTransferLead(null);
          setSelectedAssociate(null);
          setTransferRemark('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#536DFE', color: 'white' }}>
          Transfer Associate
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Lead"
                value={transferLead ? `${transferLead.name} (${transferLead.leadId})` : ''}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Select New Associate</InputLabel>
                <Select
                  value={selectedAssociate?.id || ''}
                  onChange={(e) => {
                    const associate = associateDirectory.find(a => a.id === e.target.value);
                    setSelectedAssociate(associate || null);
                  }}
                  label="Select New Associate"
                >
                  <MenuItem value="">
                    <em>Select an associate</em>
                  </MenuItem>
                  {associateDirectory.map((associate) => (
                    <MenuItem key={associate.id} value={associate.id}>
                      {associate.name} — {associate.mobile}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Remark (optional)"
                value={transferRemark}
                onChange={(e) => setTransferRemark(e.target.value)}
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setTransferDialogOpen(false);
            setTransferLead(null);
            setSelectedAssociate(null);
            setTransferRemark('');
          }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!selectedAssociate}
            onClick={handleConfirmTransfer}
          >
            Transfer
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={assignDialogOpen}
        onClose={handleCloseAssignDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' } }}
      >
        <DialogTitle
          sx={{
            bgcolor: '#536DFE',
            color: '#fff',
            fontWeight: 700,
            fontSize: 18,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            py: 2,
          }}
        >
          <PersonIcon sx={{ fontSize: 20 }} />
          Lead Assign To
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} mt={3}>
              <Autocomplete
                fullWidth
                options={assigneeDirectory}
                getOptionLabel={(option) => `${option.name} - ${option.role}`}
                loading={loadingAssignees}
                value={selectedAssignee}
                inputValue={assigneeSearchTerm}
                onInputChange={(event, newValue) => setAssigneeSearchTerm(newValue)}
                filterOptions={(options) => options} // Handled by API
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                onChange={(event, newValue) => setSelectedAssignee(newValue)}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {option.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {option.role} • {option.email}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Assign To"
                    placeholder="Select assignee"
                    required
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingAssignees ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} mt={3}>
              <TextField
                fullWidth
                label="Remark"
                value={assignRemark}
                onChange={(e) => setAssignRemark(e.target.value)}
                multiline
                rows={1}
                placeholder="Enter assignment remark (optional)"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#000' }}>
                Selected Leads ({selectedLeads.length})
              </Typography>
              <TableContainer component={Paper} sx={{ boxShadow: 2, borderRadius: 2, maxHeight: 400, overflowX: 'auto' }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 700, fontSize: 12, minWidth: 60 }}>S.No</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 12, minWidth: 80 }}>Lead No</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 12, minWidth: 120 }}>Date Time Source</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 12, minWidth: 100 }}>Lead Create By</TableCell>

                      <TableCell sx={{ fontWeight: 700, fontSize: 12, minWidth: 120 }}>Associate Mobile</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 12, minWidth: 120 }}>Prospect Name</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 12, minWidth: 150 }}>Address</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 12, minWidth: 100 }}>Customer Number</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 12, minWidth: 100 }}>WhatsApp</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 12, minWidth: 100 }}>Project</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 12, minWidth: 120 }}>Interested In</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 12, minWidth: 120 }}>Other Remark</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 12, minWidth: 100 }}>Budget Upto</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 12, minWidth: 100 }}>Inquiry Status</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 12, minWidth: 100 }}>Plan Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedLeads.length > 0 ? (
                      selectedLeads.map((lead, index) => (
                        <TableRow key={lead.id} hover>
                          <TableCell sx={{ fontWeight: 500, fontSize: 12 }}>{index + 1}</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: '#536DFE', fontSize: 12 }}>{lead.leadNo || lead.leadId}</TableCell>
                          <TableCell sx={{ fontSize: 12 }}>{lead.createdDate} {lead.source}</TableCell>
                          <TableCell sx={{ fontSize: 12 }}>{lead.company}</TableCell>

                          <TableCell sx={{ fontSize: 12 }}>{lead.assignedMobile}</TableCell>
                          <TableCell sx={{ fontSize: 12 }}>{lead.name}</TableCell>
                          <TableCell sx={{ fontSize: 12, maxWidth: 150 }} title={lead.address}>
                            {lead.address && lead.address.length > 20 ? `${lead.address.substring(0, 20)}...` : lead.address}
                          </TableCell>
                          <TableCell sx={{ fontSize: 12 }}>{lead.phone}</TableCell>
                          <TableCell sx={{ fontSize: 12 }}>{lead.whatsapp}</TableCell>
                          <TableCell sx={{ fontSize: 12 }}>{lead.interestedIn}</TableCell>
                          <TableCell sx={{ fontSize: 12 }}>{lead.interestedIn}</TableCell>
                          <TableCell sx={{ fontSize: 12, maxWidth: 120 }} title={lead.notes || lead.requirement}>
                            {(lead.notes || lead.requirement) && (lead.notes || lead.requirement).length > 15
                              ? `${(lead.notes || lead.requirement).substring(0, 15)}...`
                              : (lead.notes || lead.requirement)}
                          </TableCell>
                          <TableCell sx={{ fontSize: 12 }}>{lead.budget}</TableCell>
                          <TableCell>
                            <Chip
                              label={lead.status}
                              size="small"
                              sx={{
                                backgroundColor: getStatusColor(lead.status).backgroundColor,
                                color: getStatusColor(lead.status).color,
                                fontWeight: 600,
                                fontSize: '11px',
                                height: '20px',
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ fontSize: 12 }}>{lead.nextFollowUp}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={15} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">No leads selected</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #e0e0e0', bgcolor: '#f8f9fa' }}>
          <Button
            onClick={handleCloseAssignDialog}
            variant="outlined"
            sx={{
              textTransform: 'none',
              px: 3,
              fontWeight: 600,
              borderRadius: 2,
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleBulkAssign}
            disabled={assignLoading || selectedLeads.length === 0 || !selectedAssignee}
            startIcon={assignLoading ? <CircularProgress size={18} color="inherit" /> : <PersonIcon />}
            sx={{
              textTransform: 'none',
              px: 3,
              fontWeight: 600,
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(83, 109, 254, 0.3)',
            }}
          >
            {assignLoading ? 'Assigning...' : `Assign ${selectedLeads.length} Lead${selectedLeads.length > 1 ? 's' : ''}`}
          </Button>
        </DialogActions>
      </Dialog>

      <AlertModal
        open={alertData.open}
        onClose={() => setAlertData({ ...alertData, open: false })}
        title={alertData.type}
        message={alertData.message}
        type={alertData.type.toLowerCase() === 'success' ? 'success' : alertData.type.toLowerCase() === 'error' ? 'error' : alertData.type.toLowerCase() === 'warning' ? 'warning' : 'info'}
      />

      <ConfirmModal
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText}
        cancelText={confirmDialog.cancelText}
        onConfirm={() => {
          if (confirmDialog.action) confirmDialog.action();
          setConfirmDialog({ ...confirmDialog, open: false });
        }}
        type={confirmDialog.type}
      />
    </Box>
  );
};
export default LeadList